import type { CSSProperties, ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import type { HotkeyRegistryEntry } from "capitalrx-hotkeys";

import { COMPONENT_CLASSES, DEFAULT_COMPONENT_CONFIG, DISPLAY_NAMES, ENABLE_MODIFIER_POPOVER } from "../constants";
import type { ModifierKey, ModifierPopoverProps } from "../types";
import { classNames, formatModifier, getPlatform } from "../utils";
import { HotkeyBadge } from "./HotkeyBadge";
import { useHotkeyContext } from "./HotkeyProvider";

import "../styles.scss";

const MODIFIER_KEYS: Record<string, ModifierKey> = {
  Control: "ctrl",
  Alt: "alt",
  Shift: "shift",
  Meta: "meta",
};

export function ModifierPopover({
  holdDuration = DEFAULT_COMPONENT_CONFIG.modifierPopover.holdDuration,
  position = DEFAULT_COMPONENT_CONFIG.modifierPopover.position,
  showDescriptions = true,
  maxItems = DEFAULT_COMPONENT_CONFIG.modifierPopover.maxItems,
  groupByCategory = false,
  zIndex,
  onShow,
  onHide,
}: ModifierPopoverProps): ReactNode {
  const { manager } = useHotkeyContext();
  const [isVisible, setIsVisible] = useState(false);
  const [activeModifier, setActiveModifier] = useState<ModifierKey | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const timerRef = useRef<number | null>(null);
  const pressedKeysRef = useRef<Set<string>>(new Set());
  const platform = getPlatform();

  // Use refs to avoid recreating event listeners when callbacks change
  const onShowRef = useRef(onShow);
  const onHideRef = useRef(onHide);

  useEffect(() => {
    onShowRef.current = onShow;
    onHideRef.current = onHide;
  }, [onShow, onHide]);

  // Get all hotkeys that use the active modifier
  const matchingHotkeys = useMemo(() => {
    if (!activeModifier || !ENABLE_MODIFIER_POPOVER) return [];

    const allHotkeys: HotkeyRegistryEntry[] = manager.getHotkeys?.() || [];

    const matches = allHotkeys
      .filter((hotkey) => {
        const modifiers = hotkey.config?.modifiers || [];
        return modifiers.includes(activeModifier);
      })
      .slice(0, maxItems);

    if (groupByCategory) {
      return matches.sort((a, b) => {
        const catA = a.config?.category || "Other";
        const catB = b.config?.category || "Other";
        return catA.localeCompare(catB);
      });
    }

    return matches;
  }, [activeModifier, manager, maxItems, groupByCategory]);

  useEffect(() => {
    const keyDownHandler = (event: KeyboardEvent) => {
      const modifierKey = MODIFIER_KEYS[event.key];
      if (!modifierKey) return;

      pressedKeysRef.current.add(event.key);

      const modifierCount = Array.from(pressedKeysRef.current).filter((k) => MODIFIER_KEYS[k]).length;

      if (modifierCount === 1 && !timerRef.current) {
        timerRef.current = window.setTimeout(() => {
          setActiveModifier(modifierKey);
          setIsVisible(true);
          onShowRef.current?.(modifierKey);
        }, holdDuration);
      }
    };

    const keyUpHandler = (event: KeyboardEvent) => {
      const modifierKey = MODIFIER_KEYS[event.key];
      if (!modifierKey) return;

      pressedKeysRef.current.delete(event.key);

      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      setActiveModifier((current) => {
        if (modifierKey === current) {
          setIsVisible(false);
          onHideRef.current?.(modifierKey);
          return null;
        }
        return current;
      });
    };

    const mouseMoveHandler = (event: MouseEvent) => {
      setMousePosition({ x: event.clientX, y: event.clientY });
    };

    window.addEventListener("keydown", keyDownHandler, true);
    window.addEventListener("keyup", keyUpHandler, true);
    window.addEventListener("mousemove", mouseMoveHandler);

    return () => {
      window.removeEventListener("keydown", keyDownHandler, true);
      window.removeEventListener("keyup", keyUpHandler, true);
      window.removeEventListener("mousemove", mouseMoveHandler);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [holdDuration]);

  if (!isVisible || !activeModifier || matchingHotkeys.length === 0) {
    return null;
  }

  // Calculate position based on prop and screen dimensions
  const style: CSSProperties = {
    position: "fixed",
    zIndex: zIndex || 2147483646,
  };

  if (position === "bottom-left") {
    style.bottom = "1rem";
    style.left = "1rem";
  } else if (position === "bottom-right") {
    style.bottom = "1rem";
    style.right = "1rem";
  } else if (position === "cursor") {
    style.top = `${mousePosition.y + 20}px`;
    style.left = `${mousePosition.x + 20}px`;
  }

  const modifierLabel = formatModifier(activeModifier, { platform });

  return (
    <div
      className={classNames(COMPONENT_CLASSES.modifierPopover.container, COMPONENT_CLASSES.modifierPopover.position[position])}
      style={style}
    >
      <div className={COMPONENT_CLASSES.modifierPopover.header}>
        <samp className={"hotkey-badge hotkey-badge--sm hotkey-badge--glass"} style={{ background: "transparent" }}>
          <kbd className={"hotkey-badge__key"}>{modifierLabel}</kbd>
        </samp>
        <span className={COMPONENT_CLASSES.modifierPopover.headerText}>
          <samp title="Control" style={{ fontStyle: "normal" }}>
            CTRL
          </samp>
          <span style={{ filter: "invert(100%)" }}>➕</span>
          <span style={{ float: "right", marginRight: "0.36rem", fontWeight: 500 }}>𝐢 Key References</span>
        </span>
      </div>

      <div className={COMPONENT_CLASSES.modifierPopover.list}>
        {matchingHotkeys.map((hotkey) => {
          const config = hotkey.config || ({} as HotkeyRegistryEntry["config"]);
          const description = config.description || "Unnamed";
          const category = config.category;
          const isDisabled = !hotkey.enabled;

          return (
            <div
              key={hotkey.id}
              className={COMPONENT_CLASSES.modifierPopover.item}
              style={isDisabled ? { opacity: 0.5, filter: "grayscale(100%)" } : undefined}
            >
              <HotkeyBadge combo={config.key} theme="glass" size="sm" disabled={isDisabled} />
              <div className={COMPONENT_CLASSES.modifierPopover.itemContent}>
                {showDescriptions && (
                  <>
                    <div className={COMPONENT_CLASSES.modifierPopover.itemTitle}>
                      {description}
                      {isDisabled && <span style={{ fontSize: "0.75em", marginLeft: "0.5ch" }}>(disabled)</span>}
                    </div>
                    {groupByCategory && category && <div className={COMPONENT_CLASSES.modifierPopover.itemCategory}>{category}</div>}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {matchingHotkeys.length >= maxItems && (
        <div className={COMPONENT_CLASSES.modifierPopover.footer}>
          +
          {((manager.getHotkeys?.() as HotkeyRegistryEntry[]) || []).filter((h) => h.config?.modifiers?.includes(activeModifier)).length
            - maxItems}{" "}
          more
        </div>
      )}
    </div>
  );
}

ModifierPopover.displayName = DISPLAY_NAMES.ModifierPopover;
